import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { CostStats as CostStatsType } from '../../types';
import {
  CHART_AXIS_PROPS,
  CHART_AXIS_TICK,
  CHART_BAR_PROPS,
  CHART_CATEGORICAL,
  CHART_GRID_PROPS,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from './chartTheme';
import {
  ChartBarGradient,
  ChartEmptyState,
  ChartPanel,
} from './ChartPrimitives';
import { chartGradientFill, useChartGradientId } from './chartGradient';

interface CostStatsProps {
  stats: CostStatsType;
  title?: string;
}

function formatCost(amount: number): string {
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  if (amount < 1) return `$${amount.toFixed(3)}`;
  return `$${amount.toFixed(2)}`;
}

export function CostStats({ stats, title = 'Cost Analysis' }: CostStatsProps) {
  const gradientId = useChartGradientId('cost-bars');

  return (
    <ChartPanel
      title={title}
      iconClassName="bg-warning-50 text-warning-700"
      icon={
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      }
    >

      {/* Summary Cards */}
      <div className="mb-3 grid flex-shrink-0 grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5">
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Average</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-800">{formatCost(stats.average)}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5">
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Median</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-800">{formatCost(stats.median)}</p>
        </div>
        <div className="rounded-lg border border-danger-100 bg-danger-50 p-2.5">
          <p className="text-[10px] font-medium uppercase tracking-wide text-danger-600">Maximum</p>
          <p className="mt-0.5 text-sm font-semibold text-danger-700">{formatCost(stats.max)}</p>
        </div>
        <div className="rounded-lg border border-warning-200 bg-warning-50 p-2.5">
          <p className="text-[10px] font-medium uppercase tracking-wide text-warning-600">Total</p>
          <p className="mt-0.5 text-sm font-semibold text-warning-700">{formatCost(stats.total)}</p>
        </div>
      </div>

      {/* Histogram */}
      {stats.histogram.length > 0 ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.histogram} margin={{ top: 5, right: 5, left: -10, bottom: 25 }}>
              <defs>
                <ChartBarGradient id={gradientId} color={CHART_CATEGORICAL[3]} />
              </defs>
              <CartesianGrid {...CHART_GRID_PROPS} />
              <XAxis
                {...CHART_AXIS_PROPS}
                dataKey="range"
                tick={CHART_AXIS_TICK}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={40}
              />
              <YAxis {...CHART_AXIS_PROPS} tick={CHART_AXIS_TICK} allowDecimals={false} width={30} />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                formatter={(value: number) => [`${value} scans`, 'Count']}
              />
              <Bar
                {...CHART_BAR_PROPS}
                dataKey="count"
                fill={chartGradientFill(gradientId)}
                radius={[3, 3, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <ChartEmptyState>
          <div className="text-center">
            <p>No cost data available</p>
            <p className="text-sm mt-1">Configure pricing in Settings to see cost analysis</p>
          </div>
        </ChartEmptyState>
      )}
    </ChartPanel>
  );
}
